import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { GRADIENTS } from '../../theme/themeConfig';
import { useThemeMode } from '../../theme/Thememodecontext';

// ── Animations ────────────────────────────────────────────────────────────────

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1);   opacity: 1;    }
  50%       { transform: scale(1.12); opacity: 0.85; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0);   opacity: 0.4; }
  40%            { transform: translateY(-6px); opacity: 1;   }
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface AppLoaderProps {
  /** Company name shown below the spinner. Falls back to 'Loading…' */
  companyName?: string;
  /** Company logo URL. When provided, renders it inside the spinner ring. */
  logoUrl?: string;
}

const AppLoader: React.FC<AppLoaderProps> = ({ companyName, logoUrl }) => {
  debugger
  const { colors, isDark } = useThemeMode();

  const bgColor = isDark ? colors.background.default : '#f8fafc';
  const ringBase = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const initial  = (companyName ?? 'M').charAt(0).toUpperCase();

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        gap: 4,
      }}
    >
      {/* ── Spinner stack ── */}
      <Box sx={{ position: 'relative', width: 112, height: 112 }}>

        {/* Outer ring — slow clockwise */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `3px solid ${ringBase}`,
            borderTopColor: colors.primary.main,
            borderRightColor: colors.primary.main,
            animation: `${spin} 1.6s linear infinite`,
          }}
        />

        {/* Middle ring — faster counter-clockwise */}
        <Box
          sx={{
            position: 'absolute',
            inset: 12,
            borderRadius: '50%',
            border: `2.5px solid ${ringBase}`,
            borderBottomColor: colors.primary.light ?? colors.primary.main,
            borderLeftColor:   colors.primary.light ?? colors.primary.main,
            animation: `${spinReverse} 1.1s linear infinite`,
            opacity: 0.75,
          }}
        />

        {/* Inner dot / logo */}
        <Box
          sx={{
            position: 'absolute',
            inset: 24,
            borderRadius: '50%',
            background: GRADIENTS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${pulse} 2s ease-in-out infinite`,
            boxShadow: `0 0 24px ${colors.primary.main}55`,
            overflow: 'hidden',
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt="logo"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
            />
          ) : (
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 800,
                fontSize: '1.5rem',
                lineHeight: 1,
                userSelect: 'none',
              }}
            >
              {initial}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Label + dots ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          animation: `${fadeInUp} 0.6s ease both`,
          animationDelay: '0.2s',
          opacity: 0,
        }}
      >
        {/* Shimmer company name */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.1rem',
            background: `linear-gradient(90deg, ${colors.neutral.gray[400]} 25%, ${colors.primary.main} 50%, ${colors.neutral.gray[400]} 75%)`,
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: `${shimmer} 2.2s linear infinite`,
            letterSpacing: '0.02em',
          }}
        >
          {companyName || 'Loading'}
        </Typography>

        {/* Bouncing dots */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: colors.primary.main,
                animation: `${dotBounce} 1.2s ease-in-out infinite`,
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLoader;